/* import __COLOCATED_TEMPLATE__ from './environment-config.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type StandardizedPlatform from 'embercom/objects/standalone/platforms/standardized-platform';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';

interface Signature {
  Args: {
    workflow: Workflow;
    isDisabled?: boolean;
    readOnly?: boolean;
    inlineEdit?: boolean;
  };
}

export default class EnvironmentConfig extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare contentEditorService: {
    activeObject?: { botData?: { targetEnvironment: string } };
  };

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    // Initialize targetEnvironment with activeConfiguration if not set
    if (!this.isValidEnvironment && this.platform.activeConfiguration?.id) {
      this.setTargetEnvironment(this.platform.activeConfiguration.id);
    }
  }

  get isValidEnvironment() {
    let customBotData = this.args.workflow.additionalData.customBotData;
    let currentEnvironment = customBotData.targetEnvironment;
    return this.configurations.toArray().some((config) => config.id === currentEnvironment);
  }

  get platform(): StandardizedPlatform<any> {
    return this.finStandaloneService.platform as StandardizedPlatform<any>;
  }

  get configurations() {
    return this.platform.configurations ?? [];
  }

  get activeConfigurations() {
    return this.configurations.filter((config) => config.isConnected);
  }

  get selectedEnvironmentId() {
    let customBotData = this.args.workflow.additionalData.customBotData;
    return customBotData.targetEnvironment || this.platform.activeConfiguration?.id;
  }

  get selectedEnvironmentLabel() {
    let selectedId = this.selectedEnvironmentId;
    let selectedConfig = this.activeConfigurations.find((c) => c.id === selectedId);
    return selectedConfig ? this.formatLabel(selectedConfig) : '';
  }

  get environmentOptions() {
    return this.activeConfigurations.map((config) => ({
      value: config.id,
      text: this.formatLabel(config),
    }));
  }

  get isDropdownDisabled() {
    return (
      this.args.isDisabled ||
      !this.activeConfigurations.length ||
      !this.platform.activeConfiguration ||
      this.platform.activeConfiguration.isPendingSetup
    );
  }

  formatLabel(config: SalesforceConfiguration): string {
    return `${config.environmentLabel} - ${config.domain}`;
  }

  @action
  selectEnvironment(configId: string) {
    let config = this.platform.configurations?.find((c) => c.id === configId);
    if (config) {
      this.platform.switchToConfiguration(config);
      this.setTargetEnvironment(configId);
    }
  }

  /**
   * Sets targetEnvironment on both the workflow's customBotData and the custom-bot's botData.
   * Both are needed because:
   * - When draft mode is enabled: beforeSave() copies workflow.additionalData.customBotData → customBot.botData
   * - When draft mode is disabled: customBot.botData is serialized directly
   */
  setTargetEnvironment(configId: string) {
    // Set on workflow's customBotData (used when draft mode is enabled)
    let customBotData = this.args.workflow.additionalData.customBotData;
    customBotData.targetEnvironment = configId;

    // Set on custom-bot's botData (used when draft mode is disabled)
    let customBot = this.contentEditorService.activeObject;
    if (customBot && customBot.botData) {
      customBot.botData.targetEnvironment = configId;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::StandaloneCommon::EnvironmentConfig': typeof EnvironmentConfig;
  }
}
